import React, { useCallback, useEffect, useMemo } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { Col, Pagination } from 'antd'
import { includes, isNil, join, toString } from 'lodash'
import PropTypes from 'prop-types'

import { showErrorNotification } from '../../../core/helpers'
import { useQueryParams } from '../../../core/hooks'

import { StyledHotkeyContainer } from '../../../common/components/styled'
import { COMMON_AVAILABLE_HOTKEYS, PAGINATION } from '../../../common/constants'

import { useLoggedInUser } from '../../../account/hooks'
import {
  CURATION_DETAIL_LEVEL,
  MENU_CURATION_TASK_STATUS,
} from '../../../menu-curation-tasks/constants'
import { menuCurationTaskPropType } from '../../../menu-curation-tasks/prop-types'
import {
  DEFAULT_DISH_LIST_FILTERS,
  DISH_CERTAINTY_FILTER,
} from '../../constants'
import { useDishesQuery } from '../../hooks'
import { transformDishesToListView } from '../../transformers'
import {
  StyledDishListFooter,
  StyledDishTablePaginationContainer,
} from '../styled'

import DishDrawer from './dish-drawer'
import DishFilters from './dish-filters'
import DishHotkeysLegend from './dish-hotkey-legend'
import DishMenuTitleFilter from './dish-menu-title-filter'
import DishProgress from './dish-progress'
import DishTable from './dish-table'

const DishList = ({ menuCurationTask, isDisabled }) => {
  const { settings: userSettings } = useLoggedInUser()

  const { curationDetailLevel, menu, taskStatus } = menuCurationTask || {}

  const {
    currentPage = PAGINATION.DEFAULT_PAGE,
    pageSize = userSettings?.pageSize || PAGINATION.DEFAULT_PAGE_SIZE,
    hasErrors = DEFAULT_DISH_LIST_FILTERS.HAS_ERRORS,
    isDishTypeIgnored = DEFAULT_DISH_LIST_FILTERS.IGNORED,
    search = DEFAULT_DISH_LIST_FILTERS.SEARCH,
    menuTitleId = DEFAULT_DISH_LIST_FILTERS.MENU_TITLE,
    certainty = curationDetailLevel ===
    CURATION_DETAIL_LEVEL.PARTIAL_CURATION.value
      ? DISH_CERTAINTY_FILTER.LOW_CERTAINTY.value
      : DEFAULT_DISH_LIST_FILTERS.CERTAINTY,
    dishIds,
    tier,
    curatorId = DEFAULT_DISH_LIST_FILTERS.CURATOR_ID,
    qaId = DEFAULT_DISH_LIST_FILTERS.QA_ID,
    isAvailableForWork = DEFAULT_DISH_LIST_FILTERS.IS_AVAILABLE_FOR_WORK,
    wasDoneInPreviousTwoWeeks,
    isNotChecked = DEFAULT_DISH_LIST_FILTERS.IS_NOT_CHECKED,
    dishTypeId = DEFAULT_DISH_LIST_FILTERS.DISH_TYPE_ID,
    courseTypeId = DEFAULT_DISH_LIST_FILTERS.COURSE_TYPE_ID,
    setQueryParams,
  } = useQueryParams()

  const selectedIsAvailableForWork = useMemo(
    () =>
      !includes(
        [
          MENU_CURATION_TASK_STATUS.TIER_ONE_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.CURATION_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.QA_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.QC_IN_PROGRESS.value,
        ],
        taskStatus,
      )
        ? null
        : isAvailableForWork,
    [isAvailableForWork, taskStatus],
  )

  const selectedWasDoneInPreviousTwoWeeks = useMemo(() => {
    if (
      isNil(wasDoneInPreviousTwoWeeks) &&
      includes(
        [
          MENU_CURATION_TASK_STATUS.QA_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.QC_IN_PROGRESS.value,
        ],
        taskStatus,
      )
    ) {
      return true
    }

    return wasDoneInPreviousTwoWeeks
  }, [taskStatus, wasDoneInPreviousTwoWeeks])

  const { filters, pagination } = useMemo(
    () => ({
      filters: {
        isDishTypeIgnored,
        certainty,
        hasErrors,
        isAvailableForWork: selectedIsAvailableForWork,
        wasDoneInPreviousTwoWeeks: selectedWasDoneInPreviousTwoWeeks,
        isNotChecked,
        menuId: menuCurationTask.menu.id,
        search: !isNil(search) ? toString(search) : null,
        dishIds,
        menuTitleId,
        tier,
        curatorId,
        qaId,
        dishTypeId,
        courseTypeId,
      },
      pagination: {
        pageSize,
        currentPage,
      },
    }),
    [
      isDishTypeIgnored,
      certainty,
      hasErrors,
      selectedIsAvailableForWork,
      selectedWasDoneInPreviousTwoWeeks,
      isNotChecked,
      menuCurationTask.menu.id,
      search,
      dishIds,
      menuTitleId,
      tier,
      curatorId,
      qaId,
      dishTypeId,
      courseTypeId,
      pageSize,
      currentPage,
    ],
  )

  const { data, isLoading, error } = useDishesQuery(
    filters,
    pagination,
    taskStatus,
  )
  const { result, total: totalDishCount } = data || {}

  const dishes = useMemo(
    () => transformDishesToListView(result, menu?.menuTitles),
    [menu?.menuTitles, result],
  )

  const handleChangePagination = useCallback(
    (page, size) =>
      setQueryParams({
        currentPage: size !== pageSize ? PAGINATION.DEFAULT_PAGE : page,
        pageSize: size,
      }),
    [pageSize, setQueryParams],
  )

  const paginationConfig = useMemo(() => {
    if (isNil(totalDishCount) || isNil(handleChangePagination)) {
      return false
    }

    return {
      current: Number(currentPage),
      pageSize: Number(pageSize),
      total: totalDishCount,
      onChange: handleChangePagination,
      pageSizeOptions: PAGINATION.DEFAULT_PAGINATION_SIZE_OPTIONS,
      showSizeChanger: true,
      onShowSizeChange: handleChangePagination,
      size: 'small',
    }
  }, [totalDishCount, currentPage, pageSize, handleChangePagination])

  useEffect(() => {
    if (error) {
      showErrorNotification({
        title: 'Failed to get dishes',
        description: error,
      })
    }
  }, [error])

  useHotkeys(
    join(
      [
        COMMON_AVAILABLE_HOTKEYS.CMD_RIGHT.hotkey,
        COMMON_AVAILABLE_HOTKEYS.CTRL_RIGHT.hotkey,
      ],
      ',',
    ),
    event => {
      if (!paginationConfig) {
        return
      }

      event.preventDefault()
      const nextPage = paginationConfig.current + 1
      if (
        nextPage > Math.ceil(paginationConfig.total / paginationConfig.pageSize)
      ) {
        return
      }

      handleChangePagination(nextPage, pageSize)
    },
    [paginationConfig, handleChangePagination, pageSize],
  )

  useHotkeys(
    join(
      [
        COMMON_AVAILABLE_HOTKEYS.CMD_LEFT.hotkey,
        COMMON_AVAILABLE_HOTKEYS.CTRL_LEFT.hotkey,
      ],
      ',',
    ),
    event => {
      if (!paginationConfig) {
        return
      }

      event.preventDefault()
      const prevPage = paginationConfig.current - 1
      if (prevPage < 1) {
        return
      }

      handleChangePagination(prevPage, pageSize)
    },
    [paginationConfig, handleChangePagination, pageSize],
  )

  return (
    <>
      <StyledHotkeyContainer span={24}>
        <DishProgress
          taskStatus={menuCurationTask.taskStatus}
          menuId={menu.id}
        />
        <DishHotkeysLegend />
      </StyledHotkeyContainer>
      <DishFilters
        taskStatus={menuCurationTask.taskStatus}
        curationDetailLevel={curationDetailLevel}
        menuId={menu.id}
      />
      <DishDrawer isDisabled={isDisabled} />
      <DishTable
        taskStatus={menuCurationTask.taskStatus}
        dishes={dishes}
        isLoading={isLoading}
        isDisabled={isDisabled}
        isTaskTierOneDone={menuCurationTask.isTierOneDone}
        isTaskCurationDone={menuCurationTask.isCurationDone}
        isTaskQaDone={menuCurationTask.isQaDone}
        isMenuFake={menuCurationTask.menu.isFake}
      />
      <StyledDishListFooter justify="end">
        <Col span={8}>
          <DishMenuTitleFilter size="small" />
        </Col>
        <StyledDishTablePaginationContainer span={8}>
          {paginationConfig && <Pagination {...paginationConfig} />}
        </StyledDishTablePaginationContainer>
      </StyledDishListFooter>
    </>
  )
}

DishList.propTypes = {
  menuCurationTask: menuCurationTaskPropType.isRequired,
  isDisabled: PropTypes.bool,
}

DishList.defaultProps = {
  isDisabled: false,
}

export default DishList
